import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react"; 

const clampReadinessPercent = (userScore, targetScore) => {
  const user = Number(userScore);
  const target = Number(targetScore);
  if (!Number.isFinite(user) || !Number.isFinite(target) || target <= 0) return 0;
  return Math.max(0, Math.min((user / target) * 100, 100));
};

export default function CoachDashboard({ userId }) {
  // --- STATE: Dashboard Data ---
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);

  const navigate = useNavigate();

  // 1. Fetch Programs (Moved inside useEffect to fix dependency warnings)
  useEffect(() => {
    if (!userId) return;

    const fetchPrograms = async () => {
      const { data, error } = await supabase
        .from("program_coaches")
        .select(`
          programs (
            id,
            name,
            teams (
              id,
              name,
              sport,
              location_address
            )
          )
        `)
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching programs:", error);
        return;
      }

      // Flatten the nested structure to make it easier to map
      const teams = data.flatMap(pc =>
        pc.programs?.teams?.map(team => ({
          ...team,
          programId: pc.programs.id,
          programName: pc.programs.name,
        }))
      );

      setPrograms(teams);
      
      // Auto-select the first team if none is selected
      if (teams.length > 0) {
        setSelectedProgram(teams[0]);
      }
    };

    fetchPrograms();
  }, [userId]);

  // 2. Fetch Athletes (Runs whenever the selected program changes)
  useEffect(() => {
    if (!selectedProgram) return;

    async function fetchAthletes() {
      try {
        // A. Get Rostered Athletes
        const { data: rosterData, error: rosterError } = await supabase
          .from("rostered_users")
          .select("id, user_id, number, position, users!inner(id, full_name)")
          .eq("team_id", selectedProgram.id);

        if (rosterError) throw rosterError;

        const athleteIds = rosterData.map(a => a.user_id);

        if (athleteIds.length === 0) {
          setAthletes([]);
          return;
        }

        // B. Fetch Scores & Targets in parallel for performance
        const [
          { data: skillScores },
          { data: skillTargets },
          { data: combineScores },
          { data: combineTargets }
        ] = await Promise.all([
          supabase
            .from("user_skill_scores")
            .select("id, athlete_id, user_score, program_skill_score")
            .in("athlete_id", athleteIds)
            .eq("program_id", selectedProgram.programId),
          
          supabase
            .from("program_goal_skill_scores")
            .select("id, skill_name, target_score")
            .eq("program_id", selectedProgram.programId),
            
          supabase
            .from("user_combine_score")
            .select("id, athlete_id, user_score, program_combine_scores")
            .in("athlete_id", athleteIds)
            .eq("program_id", selectedProgram.programId),
            
          supabase
            .from("program_goal_combine_scores")
            .select("id, drill_name, target_score")
            .eq("program_id", selectedProgram.programId)
        ]);

        // C. Map Skill Scores
        const skillScoresByAthlete = (skillScores || []).reduce((acc, s) => {
          if (!acc[s.athlete_id]) acc[s.athlete_id] = [];
          const targetObj = skillTargets?.find(t => t.id === s.program_skill_score);
          acc[s.athlete_id].push({
            ...s,
            skill_name: targetObj?.skill_name || "Unnamed Skill",
            percent: clampReadinessPercent(s.user_score, targetObj?.target_score),
          });
          return acc;
        }, {});

        // D. Map Combine Scores
        const combineScoresByAthlete = (combineScores || []).reduce((acc, c) => {
          if (!acc[c.athlete_id]) acc[c.athlete_id] = [];
          const targetObj = combineTargets?.find(t => t.id === c.program_combine_scores);
          acc[c.athlete_id].push({
            ...c,
            drill_name: targetObj?.drill_name || "Unnamed Drill",
            percent: clampReadinessPercent(c.user_score, targetObj?.target_score),
          });
          return acc;
        }, {});

        // E. Combine data into final athlete object
        const enrichedAthletes = rosterData.map(a => {
          const skillDetails = skillScoresByAthlete[a.user_id] || [];
          const combineDetails = combineScoresByAthlete[a.user_id] || [];

          const skillAvg = skillDetails.length
            ? skillDetails.reduce((sum, s) => sum + s.percent, 0) / skillDetails.length
            : 0;

          const combineAvg = combineDetails.length
            ? combineDetails.reduce((sum, c) => sum + c.percent, 0) / combineDetails.length
            : 0;

          return {
            ...a,
            skillDetails,
            combineDetails,
            skillPercentage: Math.round(skillAvg),
            combinePercentage: Math.round(combineAvg),
            totalProgramScore: Math.round((skillAvg + combineAvg) / 2),
          };
        });

        setAthletes(enrichedAthletes);
      } catch (err) {
        console.error("Error fetching athlete data:", err.message);
      }
    }

    fetchAthletes();
  }, [selectedProgram]);

  return (
    <div className="pt-24 px-4 pb-24 md:p-6 text-gray-900 bg-gray-50 min-h-screen">

      {/* --- HEADER --- */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Coach Dashboard</h1>
      </div>

      {/* --- PROGRAM SELECTOR --- */}
      <div className="mb-8 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Select Team</label>
        <select
          className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-blue-500 focus:border-blue-500"
          value={selectedProgram?.id || ""}
          onChange={(e) => {
            const team = programs.find(t => t.id === e.target.value);
            if (team) setSelectedProgram(team);
          }}
        >
          {programs.length === 0 && <option>No teams assigned yet</option>}
          {programs.map(t => (
            <option key={t.id} value={t.id}>
              {t.programName} — {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* --- ATHLETE LIST (Horizontal Scroll) --- */}
      <h2 className="text-xl font-bold text-gray-800 mb-4 px-1">Roster</h2>

      {!selectedProgram ? (
        <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500">Please select a team.</p>
        </div>
      ) : athletes.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500">No athletes rostered on this team yet.</p>
        </div>
      ) : (
        <div className="flex flex-row gap-4 overflow-x-auto pb-6 scrollbar-hide snap-x">
          {athletes.map(athlete => (
            <div
              key={athlete.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow min-w-[280px] md:min-w-[320px] snap-center flex-shrink-0"
            >
              <div className="flex justify-between items-start mb-2">
                <h2 className="text-lg font-bold text-gray-900 truncate pr-2">
                  {athlete.users?.full_name || "Unnamed"}
                </h2>
                <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-1 rounded">
                  #{athlete.number || "-"}
                </span>
              </div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">
                {athlete.position || "No Position"}
              </p>

              <div className="space-y-2 mb-5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Skill</span>
                  <span className="font-semibold">{athlete.skillPercentage ?? 0}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Combine</span>
                  <span className="font-semibold">{athlete.combinePercentage ?? 0}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-1">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${athlete.totalProgramScore}%` }}
                  ></div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  className="flex-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg py-2 text-sm font-medium transition"
                  onClick={() => setSelectedAthlete(athlete)}
                >
                  Details
                </button>
                <button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-sm font-medium transition"
                  onClick={() => navigate(`/coach/swot/${athlete.user_id}`)}
                >
                  SWOT
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- MODAL: PLAYER DETAILS --- */}
      {selectedAthlete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0">
              <h2 className="text-lg font-bold">{selectedAthlete.users.full_name}</h2>
              <button 
                onClick={() => setSelectedAthlete(null)} 
                className="p-1 rounded-full hover:bg-gray-100 transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Skill Breakdown</h3>
                {selectedAthlete.skillDetails.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAthlete.skillDetails.map(s => (
                      <div key={s.id} className="text-sm">
                        <div className="flex justify-between mb-1">
                          <span>{s.skill_name}</span>
                          <span className="font-semibold">{Math.round(s.percent)}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${s.percent}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No skill data available.</p>
                )}
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2">Combine Breakdown</h3>
                {selectedAthlete.combineDetails.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAthlete.combineDetails.map(c => (
                      <div key={c.id} className="text-sm">
                        <div className="flex justify-between mb-1">
                          <span>{c.drill_name}</span>
                          <span className="font-semibold">{Math.round(c.percent)}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${c.percent}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No combine data available.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
